import { Button } from '@imprivata-cloud/components';
import { Dropdown, Flex } from 'antd';
import { ChevronDown, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';

// Both glyphs match the 1rem label they sit either side of.
const ICON_SIZE = 16;

export interface AuthenticatorUiData {
  text: string;
  icon: React.ReactNode;
  onClick: () => void;
}

const AuthMethodListHeader = ({
  authenticatorsList,
}: {
  authenticatorsList: Record<string, AuthenticatorUiData>;
}) => {
  const { t } = useTranslation();

  // The design's icon-to-label gap is antd's own `marginXS` (8px), which it puts
  // on the item's icon slot — so the icons move from a `Flex` wrapper inside the
  // label into `icon`, and the gap needs no declaration. The wrapper was also
  // what made the label a flex row of its own; as the item's second child the
  // label is now the `-title-content` span antd already gives `flex: auto`,
  // which is the design's `flex-grow: 1` and its derived 217px width.
  const authenticators = Object.values(authenticatorsList).map(({ text, icon, onClick }) => ({
    key: text,
    icon,
    label: text,
    onClick,
  }));
  const items = authenticators.length
    ? authenticators
    : [{ key: 'No method', label: t('self-service.auth-method.empty'), disabled: true }];

  return (
    <div className="authenticators-table-title">
      <Flex justify="space-between" align="center">
        <span className="authenticator-label">
          {t('self-service.authenticator-list.table-title')}
        </span>
        {/*
          The overlay used to carry `user-dropdown-menu`, which is the portal
          header's *account* menu class: the component library styles it, for a
          `.user-content`/`.bottom-content` DOM this menu does not have, and its
          one rule that does land here — `box-shadow: 0 0 6px #0000004d` — is
          neither antd's default nor the design's. Hence a class of this menu's
          own.
        */}
        <Dropdown
          trigger={['click']}
          menu={{ items, className: 'add-another-method-menu' }}
          placement={'bottomLeft'}
        >
          {/*
            No `type`, so this is antd's default (outlined) button — which is
            already the design's white fill, 2px #426DA9 border and text, 6px
            radius, 48px height and 8px icon gap, since those are the theme's own
            values for that variant. Only the width and the insets are written out,
            in the stylesheet; the two glyphs take the button's colour, since lucide
            strokes with `currentColor`.
          */}
          <Button
            className="add-another-method-btn"
            icon={<Plus size={ICON_SIZE} aria-hidden="true" />}
          >
            {/*
              The label is a child rather than the `label` prop so the chevron can
              sit beside it: the library's Button renders `label || children`, so
              the two are mutually exclusive. antd wraps the string in a span of
              its own and spaces all three children by the button's `iconGap`.
            */}
            {t('self-service.authenticator-list.add-authentication-method-btn')}
            <ChevronDown size={ICON_SIZE} aria-hidden="true" />
          </Button>
        </Dropdown>
      </Flex>
    </div>
  );
};

export default AuthMethodListHeader;
